import React, { Suspense, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, ChevronDown, LogOut } from 'lucide-react';
import SandboxBanner from '../components/SandboxBanner';
import { BrandMark, useBrand, useLabel } from '../brand/BrandProvider';
import { activeItem, groupNav } from './navGroups';

export const COLLAPSED_KEY = 'shell_nav_collapsed';

function readCollapsed() {
  try {
    const stored = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch { return []; }
}

function saveCollapsed(ids) {
  try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(ids)); } catch { /* storage off: it lasts for this visit */ }
}

function NavLink({ item, current, label }) {
  const Icon = item.icon;
  return (
    <li>
      <Link to={item.to} data-testid={item.testid} aria-current={current ? 'page' : undefined}
        className={`flex items-center gap-2.5 min-h-[34px] px-3 py-1 rounded-md text-sm motion-organic ${current
          ? 'bg-primary-50 text-primary-700 font-medium' : 'text-ink-muted hover:text-primary-700 hover:bg-primary-50'}`}>
        {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
        <span className="truncate">{label(item.testid, item.label)}</span>
      </Link>
    </li>
  );
}

function NavGroup({ group, current, collapsed, onToggle, label }) {
  const holdsCurrent = group.items.some((item) => item.testid === current?.testid);
  const open = holdsCurrent || !collapsed;
  const links = group.items.map((item) => (
    <NavLink key={item.testid} item={item} current={item.testid === current?.testid} label={label} />
  ));
  if (!group.title) return <ul className="space-y-0.5">{links}</ul>;
  return (
    <section data-testid={`panel-group-${group.id}`}>
      {/* The group holding this page has nothing to fold, so its heading is plain. */}
      <button type="button" data-testid={`panel-group-${group.id}-toggle`} disabled={holdsCurrent} aria-expanded={open}
        onClick={() => onToggle(group.id)}
        className="flex w-full items-center justify-between px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ink-subtle hover:text-ink disabled:hover:text-ink-subtle">
        <span>{label(`workspace:${group.id}`, group.title)}</span>
        {!holdsCurrent && <ChevronDown className={`w-3.5 h-3.5 motion-organic ${open ? '' : '-rotate-90'}`} aria-hidden="true" />}
      </button>
      {/* Folded links stay in the page, hidden: a folded group is a view of the menu, not a smaller menu. */}
      <ul className="mt-0.5 space-y-0.5" hidden={!open}>{links}</ul>
    </section>
  );
}

/**
 * The left panel on computers: what the top menu was, for signed-in people who are not on the operations terminal. It
 * is handed the items they may see and only places them; the switchers and the bell come in as elements because App.js
 * loads them on demand.
 */
export default function SidePanel({ items, user, startPath, orgBadge, showPlan, orgSwitcher, siteSwitcher, bell, onLogout, onLogoutAll }) {
  const { brand } = useBrand();
  const label = useLabel();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const groups = groupNav(items);
  const current = activeItem(items, location);

  const toggle = (id) => {
    const next = collapsed.includes(id) ? collapsed.filter((g) => g !== id) : [...collapsed, id];
    setCollapsed(next);
    saveCollapsed(next);
  };

  return (
    <nav data-testid="app-navbar" aria-label="Main menu"
      className="fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-surface-border bg-surface">
      <SandboxBanner />
      <Link to="/" className="flex items-center gap-2 px-4 py-3" data-testid="brand-home-link">
        <BrandMark />
        <span className="font-display font-medium text-lg tracking-tight text-ink truncate" data-testid="brand-name">{brand.product_name}</span>
      </Link>
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3 space-y-3" data-testid="panel-menu">
        {groups.map((group) => (
          <NavGroup key={group.id} group={group} current={current} collapsed={collapsed.includes(group.id)} onToggle={toggle} label={label} />
        ))}
      </div>
      <div className="border-t border-surface-border px-3 py-2 space-y-2" data-testid="panel-foot">
        <Suspense fallback={null}>
          {/* Each renders nothing for someone with one organisation or one site. */}
          <div data-testid="header-organization-switcher">{orgSwitcher}</div>
          <div data-testid="header-location-switcher">{siteSwitcher}</div>
        </Suspense>
        {orgBadge?.name && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-primary-700 truncate" data-testid="header-org-badge"
            title={orgBadge.tier && showPlan ? `${orgBadge.name} · ${orgBadge.tier}` : orgBadge.name}>
            <Building2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{orgBadge.name}</span>
            {orgBadge.tier && showPlan && <span className="text-ink-muted" data-testid="header-plan-badge">· {orgBadge.tier}</span>}
          </p>
        )}
        <div className="flex items-center gap-1">
          <Suspense fallback={null}>{bell}</Suspense>
          <Link to={startPath} data-testid="header-user-name" title={user.name || user.email}
            className="flex-1 min-w-0 truncate rounded-md px-1 py-1 text-sm text-ink-muted hover:text-primary-500 motion-organic">
            {user.name || user.email}
          </Link>
          <button type="button" onClick={onLogout} title="Log out" aria-label="Log out" data-testid="header-logout-btn"
            className="p-2 rounded-md text-ink-muted hover:text-danger hover:bg-surface-sunken motion-organic">
            <LogOut className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <button type="button" onClick={onLogoutAll} title="Log out of all devices" data-testid="header-logout-all-btn"
          className="text-xs text-ink-muted hover:text-danger motion-organic">
          Log out of all devices
        </button>
      </div>
    </nav>
  );
}
